import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import toast from "react-hot-toast";
import { BookOpen, Loader2 } from "lucide-react";
import { useState } from "react";
import { supabase } from "lib/supabase";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  navy: [15, 40, 80],
  navyMid: [26, 65, 130],
  accent: [59, 130, 246],
  violet: [109, 40, 217],
  violetMid: [139, 92, 246],
  violetSoft: [237, 233, 254],
  white: [255, 255, 255],
  offWhite: [248, 250, 252],
  gray100: [241, 245, 249],
  gray300: [203, 213, 225],
  gray500: [100, 116, 139],
  gray800: [30, 41, 59],
  green: [22, 163, 74],
  greenSoft: [220, 252, 231],
  teal: [13, 148, 136],
  tealSoft: [204, 251, 241],
  rose: [190, 18, 60],
  roseSoft: [255, 228, 230],
  amber: [180, 120, 0],
  amberSoft: [254, 243, 199],
  purple: [124, 58, 237],
  purpleSoft: [243, 232, 255],
  orange: [234, 88, 12],
  orangeSoft: [255, 247, 237],
  indigo: [67, 56, 202],
  indigoSoft: [238, 242, 255],
  cyan: [8, 145, 178],
  cyanSoft: [236, 254, 255],
  red: [185, 28, 28],
  redSoft: [254, 226, 226],
};

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;

// ─── Low-level helpers ────────────────────────────────────────────────────────
const rgb = (arr) => ({ r: arr[0], g: arr[1], b: arr[2] });

function setFill(doc, arr) {
  doc.setFillColor(...arr);
}
function setTxt(doc, arr) {
  doc.setTextColor(...arr);
}

function drawRect(doc, x, y, w, h, color, radius = 0) {
  setFill(doc, color);
  if (radius > 0) doc.roundedRect(x, y, w, h, radius, radius, "F");
  else doc.rect(x, y, w, h, "F");
}

function txt(
  doc,
  str,
  x,
  y,
  { color = C.gray800, size = 9, bold = false, align = "left", maxWidth } = {},
) {
  doc.setFontSize(size);
  doc.setFont("helvetica", bold ? "bold" : "normal");
  setTxt(doc, color);
  if (maxWidth) doc.text(str, x, y, { align, maxWidth });
  else doc.text(str, x, y, { align });
}

function pageFooter(doc, pageNum, total) {
  drawRect(doc, 0, PAGE_H - 14, PAGE_W, 14, C.navy);
  txt(
    doc,
    "Global Upload Guide  ·  Document Management System",
    MARGIN,
    PAGE_H - 8,
    { color: C.gray300, size: 7 },
  );
  txt(doc, `Page ${pageNum} of ${total}`, PAGE_W - MARGIN, PAGE_H - 8, {
    color: C.gray300,
    size: 7,
    align: "right",
  });
}

function pageHeader(doc) {
  drawRect(doc, 0, 0, PAGE_W, 12, C.navy);
  txt(doc, "Document Management System  ·  Global Upload Guide", MARGIN, 8, {
    color: C.gray300,
    size: 7,
  });
  txt(
    doc,
    new Date().toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    PAGE_W - MARGIN,
    8,
    { color: C.gray300, size: 7, align: "right" },
  );
}

function checkBreak(doc, y, needed, pageNum) {
  if (y + needed > PAGE_H - 22) {
    doc.addPage();
    pageHeader(doc);
    return { y: 22, page: pageNum + 1 };
  }
  return { y, page: pageNum };
}

function sectionHeader(doc, label, number, y) {
  drawRect(doc, MARGIN, y, CONTENT_W, 10, C.gray100, 2);
  drawRect(doc, MARGIN, y, 3, 10, C.violet);
  drawRect(doc, MARGIN + 6, y + 1.5, 7, 7, C.violet, 1.5);
  txt(doc, String(number), MARGIN + 9.5, y + 6.2, {
    color: C.white,
    size: 6,
    bold: true,
    align: "center",
  });
  txt(doc, label, MARGIN + 16, y + 6.8, { color: C.navy, size: 9, bold: true });
  return y + 14;
}

// ─── Cover Page ───────────────────────────────────────────────────────────────
function drawCover(doc) {
  drawRect(doc, 0, 0, PAGE_W, PAGE_H, C.navy);

  // Decorative circle top-right
  doc.setFillColor(...C.navyMid);
  doc.circle(PAGE_W + 10, -10, 80, "F");

  // Violet circle accent bottom-left
  doc.setFillColor(...C.violet);
  doc.circle(-15, PAGE_H + 10, 55, "F");

  // Bottom strip
  drawRect(doc, 0, PAGE_H - 40, PAGE_W, 40, C.navyMid);
  drawRect(doc, 0, PAGE_H - 42, PAGE_W, 4, C.violet);

  // Badge
  drawRect(doc, MARGIN, 38, 16, 14, C.violet, 3);
  txt(doc, "GU", MARGIN + 8, 46.5, {
    color: C.white,
    size: 7,
    bold: true,
    align: "center",
  });

  // Title
  txt(doc, "Global Upload", MARGIN, 78, {
    color: C.white,
    size: 34,
    bold: true,
  });
  txt(doc, "Guidelines", MARGIN, 92, {
    color: C.violetMid,
    size: 34,
    bold: true,
  });

  doc.setDrawColor(...C.violet);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, 99, MARGIN + 44, 99);

  txt(
    doc,
    "Instructions for preparing the Global Upload ZIP package:\nResumes, Global Master Excel, and Organization Documents.",
    MARGIN,
    108,
    { color: C.gray300, size: 9, maxWidth: CONTENT_W * 0.72 },
  );

  // Pills
  const pills = [
    { label: "3 Folders", sub: "Strict structure" },
    { label: "Excel + ZIPs", sub: "Two file types" },
  ];
  let px = MARGIN;
  pills.forEach((p) => {
    drawRect(doc, px, 130, 52, 22, C.navyMid, 4);
    drawRect(doc, px, 130, 3, 22, C.violet, 0);
    txt(doc, p.label, px + 7, 138, { color: C.white, size: 8, bold: true });
    txt(doc, p.sub, px + 7, 145, { color: C.gray300, size: 7 });
    px += 56;
  });

  txt(
    doc,
    "Version 1.0  ·  2026  ·  Admin Internal Use Only",
    MARGIN,
    PAGE_H - 16,
    { color: C.gray300, size: 7 },
  );
}

// ─── Folder Diagram ───────────────────────────────────────────────────────────
function drawFolderDiagram(doc, y) {
  const folders = [
    {
      name: "Resumes",
      color: C.teal,
      soft: C.tealSoft,
      desc: "One PDF per team member. Any file name works — resumes are matched to personnel by the uploader's selection, not by filename.",
      files: ["JohnSmith.pdf", "jane_doe_resume.pdf", "bob_lee_cv.pdf"],
    },
    {
      name: " Master",
      color: C.violet,
      soft: C.violetSoft,
      desc: "Contains only the filled GlobalMasterPersonnelTemplate.xlsx, with all 6 sheets: Masters, Role, Level, Personnel, Discount, Burden.",
      files: ["GlobalMasterPersonnelTemplate.xlsx"],
    },
    {
      name: "Org-Docs",
      color: C.orange,
      soft: C.orangeSoft,
      desc: "Organization documents placed directly in this folder — not zipped. No naming rules apply to these files.",
      files: ["org_policy.pdf", "org_agreement.docx"],
    },
  ];

  // main ZIP node
  drawRect(doc, MARGIN, y, CONTENT_W, 10, C.navy, 2);
  txt(doc, "global_upload.zip  (main)", MARGIN + 5, y + 6.8, {
    color: C.white,
    size: 8,
    bold: true,
  });
  y += 12;

  folders.forEach((f, fi) => {
    // Connector line
    doc.setDrawColor(...C.gray300);
    doc.setLineWidth(0.3);
    doc.line(MARGIN + 6, y, MARGIN + 6, y + 8);
    doc.line(MARGIN + 6, y + 4, MARGIN + 12, y + 4);

    // Folder label
    drawRect(doc, MARGIN + 12, y, CONTENT_W - 12, 9, f.soft, 2);
    drawRect(doc, MARGIN + 12, y, 3, 9, f.color);
    txt(doc, f.name, MARGIN + 18, y + 6.2, {
      color: f.color[0] < 100 ? f.color : C.navy,
      size: 8,
      bold: true,
    });
    y += 11;

    // Description
    const lines = doc.splitTextToSize(f.desc, CONTENT_W - 20);
    drawRect(
      doc,
      MARGIN + 16,
      y,
      CONTENT_W - 16,
      lines.length * 4.5 + 4,
      C.offWhite,
      1,
    );
    setTxt(doc, C.gray500);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.text(lines, MARGIN + 19, y + 4);
    y += lines.length * 4.5 + 7;

    // Sample files
    f.files.forEach((fn) => {
      doc.setDrawColor(...C.gray300);
      doc.line(MARGIN + 20, y, MARGIN + 20, y + 5);
      doc.line(MARGIN + 20, y + 2.5, MARGIN + 25, y + 2.5);
      drawRect(doc, MARGIN + 25, y - 0.5, CONTENT_W - 25, 6, C.gray100, 1);
      doc.setFontSize(7);
      doc.setFont("courier", "normal");
      setTxt(doc, C.gray800);
      doc.text(fn, MARGIN + 28, y + 3.5);
      y += 7;
    });

    y += 4;
  });

  return y + 4;
}

// ─── Step-by-step instructions ────────────────────────────────────────────────
function drawSteps(doc, y) {
  const steps = [
    {
      n: "1",
      title: "Download the guide and Excel template",
      body: "Click 'Download Upload Guide' in the Global Upload modal. This PDF and GlobalMasterPersonnelTemplate.xlsx download together.",
      color: C.violet,
      soft: C.violetSoft,
    },
    {
      n: "2",
      title: "Fill in the Excel template",
      body: "Open GlobalMasterPersonnelTemplate.xlsx and complete all 6 sheets — Masters, Role, Level, Personnel, Discount, Burden — using the column guides. Delete example rows before saving.",
      color: C.teal,
      soft: C.tealSoft,
    },
    {
      n: "3",
      title: "Collect resume PDFs",
      body: "Gather one PDF resume per team member being imported and place them in the Resumes/ folder. File names are not used for matching.",
      color: C.green,
      soft: C.greenSoft,
    },
    {
      n: "4",
      title: "Add organization documents",
      body: "Place any organization documents directly inside the Organization Document/ folder. Do not zip them individually — the files go in as-is.",
      color: C.orange,
      soft: C.orangeSoft,
    },
    {
      n: "5",
      title: "Assemble the main ZIP",
      body: "Create one ZIP containing exactly the 3 folders: Resumes/, Global Master/, Organization Document/, each with its files. Do not add nested folders.",
      color: C.navy,
      soft: C.gray100,
    },
    {
      n: "6",
      title: "Upload via the Global Upload modal",
      body: "Click your profile icon in the top-right corner, select 'Global Upload', choose your main ZIP file, click 'Scan ZIP' to validate it, then review the results and click 'Approve Upload' to finish.",
      color: C.violet,
      soft: C.violetSoft,
    },
  ];

  steps.forEach((s) => {
    const bodyLines = doc.splitTextToSize(s.body, CONTENT_W - 22);
    const blockH = bodyLines.length * 4.5 + 20;
    ({ y } = checkBreak(doc, y, blockH + 4, 0)); // page check (pageNum unused here)

    drawRect(doc, MARGIN, y, CONTENT_W, blockH, s.soft, 3);
    drawRect(doc, MARGIN, y, 3, blockH, s.color);

    // Number badge
    drawRect(doc, MARGIN + 6, y + 4, 8, 8, s.color, 2);
    txt(doc, s.n, MARGIN + 10, y + 9.5, {
      color: C.white,
      size: 7,
      bold: true,
      align: "center",
    });

    txt(doc, s.title, MARGIN + 18, y + 9, {
      color: C.navy,
      size: 8.5,
      bold: true,
    });

    setTxt(doc, C.gray500);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.text(bodyLines, MARGIN + 18, y + 15);

    y += blockH + 4;
  });

  return y;
}

// ─── Excel sheet guide ────────────────────────────────────────────────────────
function drawSheetGuide(doc, y, pageNum) {
  const sheets = [
    {
      name: "Masters",
      color: C.accent,
      soft: [219, 234, 254],
      cols: [
        [
          "group",
          "Unique group name (e.g. Engineering, Finance). One row per group.",
        ],
        [
          "cost_min",
          "Minimum cost for the group, in your currency. Number only.",
        ],
        [
          "cost_max",
          "Maximum cost for the group, in your currency. Number only.",
        ],
        [
          "department",
          "Department this group belongs to. Created automatically if new.",
        ],
        [
          "experience_level",
          "Experience tier (Junior, Senior, Lead…). Created if new.",
        ],
        [
          "discount_group",
          "Discount profile name to create (e.g. Early Bird).",
        ],
        [
          "burden_group",
          "Overhead/burden group name (e.g. Health Insurance, PF).",
        ],
        [
          "burden_group Unit",
          "Unit for burden: pct (%), mult (multiplier), or flat (fixed).",
        ],
      ],
    },
    {
      name: "Role",
      color: C.teal,
      soft: C.tealSoft,
      cols: [
        ["Group", "Must exactly match a group name from the Masters sheet."],
        [
          "Role",
          "Role/position title within that group (e.g. Backend Developer).",
        ],
      ],
    },
    {
      name: "Level",
      color: C.rose,
      soft: C.roseSoft,
      cols: [
        ["Group", "Must exactly match a group name from the Masters sheet."],
        ["Level", "Seniority level name (e.g. Junior, Senior, Lead)."],
        [
          "Cost to Pay",
          "Annual cost for this level, in your currency. Number only.",
        ],
      ],
    },
    {
      name: "Personnel",
      color: C.green,
      soft: C.greenSoft,
      cols: [
        ["Name", "Full name of the team member. Required."],
        ["Email", "Work email. Must be unique — duplicates are skipped."],
        ["Phone", "Contact phone number (any format)."],
        ["Group", "Must match a group name from Masters sheet."],
        ["Role", "Must match a role for the chosen Group from Role sheet."],
        ["Level", "Must match a level for the chosen Group from Level sheet."],
        ["Department", "Must match a department name from Masters sheet."],
        [
          "Experience_level",
          "Must match an experience level from Masters sheet.",
        ],
        [
          "is_sme",
          "TRUE if this person is a Subject Matter Expert. Collaboration dates required on import.",
        ],
      ],
    },
    {
      name: "Discount",
      color: C.purple,
      soft: C.purpleSoft,
      cols: [
        [
          "discount_group",
          "Must match a discount_group value in the Masters sheet.",
        ],
        [
          "discount",
          "Display name for this discount entry (e.g. Volume Discount 10%).",
        ],
        ["percentage", "Whole number percentage (10 = 10%). No % symbol."],
      ],
    },
    {
      name: "Burden",
      color: C.orange,
      soft: C.orangeSoft,
      cols: [
        [
          "Name",
          "Burden item name (e.g. Standard Package, Executive Package).",
        ],
        ["Desc", "Short description of this burden package."],
        [
          "[Burden Group columns…]",
          "One column per burden_group from Masters. Header must match the burden group name exactly; value is the numeric rate.",
        ],
      ],
    },
  ];

  sheets.forEach((sheet) => {
    const rowH = 7;
    const blockH = sheet.cols.length * rowH + 22;
    const check = checkBreak(doc, y, blockH + 6, pageNum);
    y = check.y;
    pageNum = check.page;

    // Sheet title bar
    drawRect(doc, MARGIN, y, CONTENT_W, 11, sheet.color, 2);
    txt(doc, `Sheet: ${sheet.name}`, MARGIN + 5, y + 7.5, {
      color: C.white,
      size: 8.5,
      bold: true,
    });
    y += 13;

    // Column rows
    sheet.cols.forEach(([col, desc], i) => {
      if (i % 2 === 0) drawRect(doc, MARGIN, y, CONTENT_W, rowH, C.offWhite);
      else drawRect(doc, MARGIN, y, CONTENT_W, rowH, C.white);

      // Col name chip
      doc.setFontSize(7);
      doc.setFont("courier", "bold");
      setTxt(doc, sheet.color);
      doc.text(col, MARGIN + 4, y + 5);

      const chipW = doc.getTextWidth(col) + 2;
      // desc
      txt(doc, desc, MARGIN + chipW + 8, y + 5, {
        color: C.gray500,
        size: 7.5,
      });

      y += rowH;
    });

    y += 8;
  });

  return { y, pageNum };
}

// ─── Checklist page ───────────────────────────────────────────────────────────
function drawChecklist(doc, y) {
  const items = [
    {
      n: "1",
      title: "Verify folder structure",
      body: "Main ZIP contains exactly 3 folders: resumes/, masters/, org-docs/ — each with its own files.",
    },
    {
      n: "2",
      title: "Confirm resume files",
      body: "resumes/ folder contains one PDF per person being imported.",
    },
    {
      n: "3",
      title: "Confirm Masters Excel",
      body: "masters/ folder contains the filled GlobalMasterPersonnelTemplate.xlsx with all 6 sheets completed.",
    },
    {
      n: "4",
      title: "Confirm org documents",
      body: "org-docs/ folder contains the documents directly — no nested ZIPs.",
    },
    {
      n: "5",
      title: "Check file size",
      body: "Main ZIP file size is under 200 MB before uploading.",
    },
  ];

  items.forEach((s) => {
    const bodyLines = doc.splitTextToSize(s.body, CONTENT_W - 22);
    const blockH = bodyLines.length * 4.5 + 20;

    drawRect(doc, MARGIN, y, CONTENT_W, blockH, C.gray100, 3);
    drawRect(doc, MARGIN, y, 3, blockH, C.violet);

    // Number badge
    drawRect(doc, MARGIN + 6, y + 4, 8, 8, C.violet, 2);
    txt(doc, s.n, MARGIN + 10, y + 9.5, {
      color: C.white,
      size: 7,
      bold: true,
      align: "center",
    });

    txt(doc, s.title, MARGIN + 18, y + 9, {
      color: C.navy,
      size: 8.5,
      bold: true,
    });

    setTxt(doc, C.gray500);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.text(bodyLines, MARGIN + 18, y + 15);

    y += blockH + 4;
  });

  return y;
}

// ─── Main PDF builder ─────────────────────────────────────────────────────────
async function buildGuidePDF() {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // Page 1 — Cover
  drawCover(doc);

  // Page 2 — Folder Structure
  doc.addPage();
  pageHeader(doc);
  let y = 22;
  let pageNum = 2;

  y = sectionHeader(doc, "ZIP Folder Structure", 1, y);
  txt(
    doc,
    "Your upload must be a single ZIP file containing exactly these three top-level folders.",
    MARGIN,
    y,
    { color: C.gray500, size: 8, maxWidth: CONTENT_W },
  );
  y += 8;
  y = drawFolderDiagram(doc, y);

  // Page 3 — Step-by-step (always starts on a fresh page)
  doc.addPage();
  pageHeader(doc);
  y = 22;
  pageNum++;

  y = sectionHeader(doc, "Step-by-Step Upload Instructions", 2, y);
  y = drawSteps(doc, y);

  // Excel Sheet Guide (always starts on a fresh page, may span several)
  doc.addPage();
  pageHeader(doc);
  y = 22;
  pageNum++;
  y = sectionHeader(doc, "Excel Template Sheet Guide", 3, y);
  txt(
    doc,
    "Fill every sheet in GlobalMasterPersonnelTemplate.xlsx following the column definitions below.",
    MARGIN,
    y,
    { color: C.gray500, size: 8, maxWidth: CONTENT_W },
  );
  y += 9;
  ({ y, pageNum } = drawSheetGuide(doc, y, pageNum));

  // Pre-upload Checklist (always starts on a fresh page)
  doc.addPage();
  pageHeader(doc);
  y = 22;
  pageNum++;
  y = sectionHeader(doc, "Pre-Upload Steps", 4, y);
  txt(
    doc,
    "Complete each step below before uploading to ensure a successful import.",
    MARGIN,
    y,
    { color: C.gray500, size: 8, maxWidth: CONTENT_W },
  );
  y += 9;
  drawChecklist(doc, y);

  // Stamp footers on all content pages
  const total = doc.getNumberOfPages();
  for (let i = 2; i <= total; i++) {
    doc.setPage(i);
    pageFooter(doc, i, total);
  }

  doc.save("Global-Upload-Guide.pdf");
}

// ─── Download helper ──────────────────────────────────────────────────────────
function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ─── 1. Masters Excel (existing file in /templates/) ─────────────────────────
function downloadMastersExcel() {
  triggerDownload("/templates/MasterCSVUpload.xlsx", "MasterCSVUpload.xlsx");
}

// ─── 2. Org Docs Bulk Upload PDF (reuses the same logic as UploadGuideButton) ─
// Imported inline here so GlobalUploadGuideButton has no external dependency.
async function buildOrgDocsPDF() {
  const [
    { data: titles, error: titleError },
    { data: types, error: typeError },
    { data: subTypes, error: subTypeError },
  ] = await Promise.all([
    supabase
      .from("ms_docs_title")
      .select("id,name,code")
      .eq("is_active", 0)
      .order("name"),
    supabase
      .from("ms_docs_type")
      .select("id,name,code,docs_title_id")
      .eq("is_active", 0)
      .order("name"),
    supabase
      .from("ms_docs_sub_type")
      .select("id,name,code,docs_type_id")
      .eq("is_active", 0)
      .order("name"),
  ]);
  if (titleError) throw titleError;
  if (typeError) throw typeError;
  if (subTypeError) throw subTypeError;

  // Reuse the same drawCoverPage / helpers from UploadGuideButton — replicated
  // here so this file is self-contained. Palette kept identical.
  const C2 = {
    navy: [15, 40, 80],
    navyMid: [26, 65, 130],
    accent: [59, 130, 246],
    accentSoft: [219, 234, 254],
    white: [255, 255, 255],
    offWhite: [248, 250, 252],
    gray100: [241, 245, 249],
    gray300: [203, 213, 225],
    gray500: [100, 116, 139],
    gray800: [30, 41, 59],
  };
  const PW = 210,
    PH = 297,
    M = 18,
    CW = PW - M * 2;
  const rgb2 = (a) => ({ r: a[0], g: a[1], b: a[2] });

  const doc2 = new jsPDF({ unit: "mm", format: "a4" });

  // Cover
  doc2.setFillColor(...C2.navy);
  doc2.rect(0, 0, PW, PH, "F");
  doc2.setFillColor(...C2.navyMid);
  doc2.circle(PW + 10, -10, 80, "F");
  doc2.setFillColor(...C2.navyMid);
  doc2.rect(0, PH - 40, PW, 40, "F");
  doc2.setFillColor(...C2.accent);
  doc2.rect(0, PH - 42, PW, 4, "F");
  doc2.setFillColor(...C2.accent);
  doc2.roundedRect(M, 38, 14, 14, 3, 3, "F");
  doc2.setFontSize(6);
  doc2.setFont("helvetica", "bold");
  doc2.setTextColor(...C2.white);
  doc2.text("DMS", M + 7, 46.5, { align: "center" });
  doc2.setFontSize(36);
  doc2.setFont("helvetica", "bold");
  doc2.setTextColor(...C2.white);
  doc2.text("Bulk Upload", M, 78);
  doc2.setTextColor(...C2.accent);
  doc2.text("Guidelines", M, 93);
  doc2.setDrawColor(...C2.accent);
  doc2.setLineWidth(0.5);
  doc2.line(M, 100, M + 40, 100);
  doc2.setFontSize(9);
  doc2.setFont("helvetica", "normal");
  doc2.setTextColor(...C2.gray300);
  doc2.text(
    "Follow the instructions below when preparing ZIP file uploads\nfor the Document Management System.",
    M,
    109,
    { maxWidth: CW * 0.7 },
  );
  const pills2 = [
    { label: "ZIP Only", sub: "Archive format" },
    { label: "Valid Prefix", sub: "Required naming" },
    { label: "Auto-Routed", sub: "By category code" },
  ];
  let px = M;
  pills2.forEach((p) => {
    doc2.setFillColor(...C2.navyMid);
    doc2.roundedRect(px, 130, 52, 22, 4, 4, "F");
    doc2.setFillColor(...C2.accent);
    doc2.rect(px, 130, 3, 22, "F");
    doc2.setFontSize(8);
    doc2.setFont("helvetica", "bold");
    doc2.setTextColor(...C2.white);
    doc2.text(p.label, px + 7, 138);
    doc2.setFontSize(7);
    doc2.setFont("helvetica", "normal");
    doc2.setTextColor(...C2.gray300);
    doc2.text(p.sub, px + 7, 145);
    px += 56;
  });
  doc2.setFontSize(7);
  doc2.setTextColor(...C2.gray300);
  doc2.text("Version 1.0  ·  2026  ·  Internal Use Only", M, PH - 16);

  // Content page
  doc2.addPage();
  let y2 = 20;

  const hdrBar = (d) => {
    d.setFillColor(...C2.navy);
    d.rect(0, 0, PW, 12, "F");
    d.setFontSize(7);
    d.setFont("helvetica", "normal");
    d.setTextColor(...C2.gray300);
    d.text("Document Management System  ·  Bulk Upload Guide", M, 8);
    d.text(
      new Date().toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      PW - M,
      8,
      { align: "right" },
    );
  };
  hdrBar(doc2);

  const secHdr = (label, num, y) => {
    doc2.setFillColor(...C2.gray100);
    doc2.roundedRect(M, y, CW, 10, 2, 2, "F");
    doc2.setFillColor(...C2.accent);
    doc2.rect(M, y, 3, 10, "F");
    doc2.setFillColor(...C2.accent);
    doc2.roundedRect(M + 6, y + 1.5, 7, 7, 1.5, 1.5, "F");
    doc2.setFontSize(6);
    doc2.setFont("helvetica", "bold");
    doc2.setTextColor(...C2.white);
    doc2.text(String(num), M + 9.5, y + 6.2, { align: "center" });
    doc2.setFontSize(9);
    doc2.setTextColor(...C2.navy);
    doc2.text(label, M + 16, y + 6.8);
    return y + 14;
  };

  // Naming format box
  y2 = secHdr("File Naming Format", 1, y2);
  doc2.setFillColor(...C2.accentSoft);
  doc2.roundedRect(M, y2, CW, 22, 3, 3, "F");
  doc2.setDrawColor(...C2.accent);
  doc2.setLineWidth(0.4);
  doc2.roundedRect(M, y2, CW, 22, 3, 3, "S");
  doc2.setFontSize(7);
  doc2.setFont("helvetica", "bold");
  doc2.setTextColor(...C2.navyMid);
  doc2.text("File Naming Format", M + 5, y2 + 7);
  const segs = [
    { t: "TITLECODE", c: C2.navy },
    { t: "_", c: C2.gray500 },
    { t: "TYPECODE", c: C2.navyMid },
    { t: "_", c: C2.gray500 },
    { t: "SUBTYPECODE", c: C2.accent },
    { t: ".pdf", c: C2.gray500 },
  ];
  let cx = M + 5;
  doc2.setFont("courier", "bold");
  segs.forEach(({ t, c }) => {
    doc2.setFontSize(10);
    doc2.setTextColor(...c);
    doc2.text(t, cx, y2 + 16);
    cx += doc2.getTextWidth(t) + 0.3;
  });
  y2 += 28;

  const legend = [
    { code: "TITLECODE", desc: "Top-level category code  (e.g. FIN, HR, OPS)" },
    { code: "TYPECODE", desc: "Document type code within the category" },
    {
      code: "SUBTYPECODE",
      desc: "Specific sub-type code for fine-grained routing",
    },
  ];
  legend.forEach(({ code, desc }, i) => {
    if (i % 2 === 0) {
      doc2.setFillColor(...C2.offWhite);
      doc2.rect(M, y2, CW, 9, "F");
    }
    doc2.setFontSize(7.5);
    doc2.setFont("helvetica", "bold");
    doc2.setTextColor(...C2.navyMid);
    doc2.text(code, M + 4, y2 + 6);
    doc2.setFont("helvetica", "normal");
    doc2.setTextColor(...C2.gray500);
    doc2.text(desc, M + 46, y2 + 6);
    y2 += 9;
  });
  y2 += 8;

  // Instructions
  y2 = secHdr("Upload Instructions", 2, y2);
  const rows = [
    ["Separator", "Use underscore ( _ ) between code segments only", "warn"],
    ["Spacing", "No spaces allowed anywhere in the filename", "error"],
    ["Archive", "Files must be inside a ZIP archive to upload", "info"],
    ["Formats", "Accepted: .pdf  .png  .jpg  .jpeg  .docx  .xlsx", "info"],
    [
      "Invalid files",
      "Files with unrecognised prefixes go to Unclassified",
      "warn",
    ],
  ];
  const bC = {
    info: C2.accentSoft,
    warn: [254, 243, 199],
    error: [254, 226, 226],
  };
  const bT = { info: C2.navyMid, warn: [180, 120, 0], error: [185, 28, 28] };
  rows.forEach((row, i) => {
    const ry = y2 + i * 11;
    if (i % 2 === 0) {
      doc2.setFillColor(...C2.offWhite);
      doc2.rect(M, ry, CW, 11, "F");
    }
    doc2.setFillColor(...bC[row[2]]);
    doc2.roundedRect(M + 3, ry + 2.5, 18, 6, 1.5, 1.5, "F");
    doc2.setFontSize(5.5);
    doc2.setFont("helvetica", "bold");
    doc2.setTextColor(...bT[row[2]]);
    doc2.text(row[0].toUpperCase(), M + 12, ry + 6.3, { align: "center" });
    doc2.setFontSize(8);
    doc2.setFont("helvetica", "normal");
    doc2.setTextColor(...C2.gray800);
    doc2.text(row[1], M + 24, ry + 7);
  });
  y2 += rows.length * 11 + 10;

  // Hierarchy
  y2 = secHdr("Document Code Hierarchy", 3, y2);
  doc2.setFontSize(7.5);
  doc2.setFont("helvetica", "normal");
  doc2.setTextColor(...C2.gray500);
  doc2.text(
    "Each file must begin with a valid Title code, followed by Type and Sub-type codes separated by underscores.",
    M,
    y2,
    { maxWidth: CW },
  );
  y2 += 10;

  let pn2 = 2;
  (titles || []).forEach((title) => {
    if (y2 + 40 > PH - 22) {
      doc2.addPage();
      hdrBar(doc2);
      y2 = 20;
      pn2++;
    }
    // Title card
    doc2.setFillColor(...C2.navy);
    doc2.roundedRect(M, y2, CW, 12, 2, 2, "F");
    doc2.setFillColor(...C2.accent);
    doc2.rect(M, y2, 3, 12, "F");
    doc2.setFontSize(9);
    doc2.setFont("helvetica", "bold");
    doc2.setTextColor(...C2.white);
    doc2.text(title.name ?? "—", M + 6, y2 + 8);
    const code = title.code ?? "N/A";
    doc2.setFontSize(7);
    doc2.setFont("helvetica", "bold");
    const cw = doc2.getTextWidth(code) + 8;
    const cx2 = M + CW - cw - 3;
    doc2.setFillColor(...C2.accent);
    doc2.roundedRect(cx2, y2 + 3, cw, 6, 1.5, 1.5, "F");
    doc2.setTextColor(...C2.white);
    doc2.text(code, cx2 + cw / 2, y2 + 7.3, { align: "center" });
    y2 += 14;

    const relTypes = (types || []).filter((t) => t.docs_title_id === title.id);
    relTypes.forEach((type) => {
      if (y2 + 30 > PH - 22) {
        doc2.addPage();
        hdrBar(doc2);
        y2 = 20;
        pn2++;
      }
      doc2.setFillColor(...C2.gray100);
      doc2.roundedRect(M + 3, y2, CW - 3, 9, 1.5, 1.5, "F");
      doc2.setFontSize(8);
      doc2.setFont("helvetica", "bold");
      doc2.setTextColor(...C2.navyMid);
      doc2.text(`  ${type.name ?? "—"}`, M + 8, y2 + 6);
      const tc = type.code ?? "N/A";
      doc2.setFontSize(6.5);
      const tw = doc2.getTextWidth(tc) + 6;
      const tx = M + CW - tw - 3;
      doc2.setFillColor(...C2.accentSoft);
      doc2.roundedRect(tx, y2 + 1.5, tw, 6, 1.5, 1.5, "F");
      doc2.setTextColor(...C2.navyMid);
      doc2.text(tc, tx + tw / 2, y2 + 5.8, { align: "center" });
      y2 += 11;

      const subs = (subTypes || []).filter((s) => s.docs_type_id === type.id);
      if (subs.length > 0) {
        autoTable(doc2, {
          startY: y2,
          margin: { left: M + 6, right: M },
          tableWidth: CW - 6,
          head: [["Sub-type Name", "Code"]],
          body: subs.map((s) => [s.name ?? "—", s.code ?? "N/A"]),
          styles: {
            fontSize: 7.5,
            cellPadding: { top: 2.5, bottom: 2.5, left: 5, right: 5 },
            textColor: rgb2(C2.gray800),
            lineColor: rgb2(C2.gray300),
            lineWidth: 0.2,
            fillColor: [255, 255, 255],
          },
          headStyles: {
            fillColor: rgb2(C2.navyMid),
            textColor: rgb2(C2.white),
            fontStyle: "bold",
            fontSize: 7,
          },
          columnStyles: {
            0: { cellWidth: "auto" },
            1: {
              cellWidth: 28,
              fontStyle: "bold",
              textColor: rgb2(C2.navyMid),
              halign: "left",
            },
          },
          theme: "grid",
        });
        y2 = doc2.lastAutoTable.finalY + 6;
      }
    });
    y2 += 6;
  });

  const total2 = doc2.getNumberOfPages();
  for (let i = 2; i <= total2; i++) {
    doc2.setPage(i);
    doc2.setFillColor(...C2.navy);
    doc2.rect(0, PH - 14, PW, 14, "F");
    doc2.setFontSize(7);
    doc2.setFont("helvetica", "normal");
    doc2.setTextColor(...C2.gray300);
    doc2.text("Document Management System  ·  Bulk Upload Guide", M, PH - 8);
    doc2.text(`Page ${i} of ${total2}`, PW - M, PH - 8, { align: "right" });
  }

  doc2.save("Bulk-Upload-Guide.pdf");
}

// ─── Component ────────────────────────────────────────────────────────────────
const GlobalUploadGuideButton = () => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    try {
      setLoading(true);
      // All 3 downloads fire together:
      // 1. Global Upload ZIP guide PDF
      // 2. MasterCSVUpload.xlsx (existing template)
      // 3. Bulk Upload Org Docs PDF (naming guide with hierarchy)
      await Promise.all([
        buildGuidePDF(),
        Promise.resolve(downloadMastersExcel()),
        buildOrgDocsPDF(),
      ]);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to generate guides");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="
        inline-flex items-center gap-2.5
        px-5 py-2.5 rounded-xl
       text-white
        text-sm font-semibold
        shadow-md
       bg-primary text-primary-foreground hover:opacity-90
        active:scale-95
        transition-all duration-150
        disabled:opacity-60 disabled:cursor-not-allowed
      "
      title="Downloads the PDF guide + Excel template simultaneously"
    >
      {loading ? (
        <Loader2 size={16} className="animate-spin flex-shrink-0" />
      ) : (
        <BookOpen size={16} className="flex-shrink-0" />
      )}
      {loading ? "Generating…" : "Download Upload Guide"}
    </button>
  );
};

export default GlobalUploadGuideButton;
